import type { CSSProperties, ReactNode } from 'react';

export interface CheckboxProps {
    checked?: boolean;
    /** Estado misto: nem marcado nem desmarcado. */
    indeterminate?: boolean;
    onChange?: (checked: boolean) => void;
    label?: ReactNode;
    description?: ReactNode;
    /** Nome acessível quando não há `label` visível — sem um dos dois, o leitor
     *  de tela anuncia só "caixa de seleção", e numa lista de dez tarefas isso
     *  não diz qual. */
    ariaLabel?: string;
    disabled?: boolean;
    style?: CSSProperties;
}

export function Checkbox({
    checked = false, indeterminate = false, onChange, label, description, ariaLabel, disabled = false, style,
}: CheckboxProps) {
    const on = checked || indeterminate;
    return (
        <label
            style={{
                display: 'inline-flex', alignItems: description ? 'flex-start' : 'center', gap: 9,
                position: 'relative',
                cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, ...style,
            }}
        >
            <span
                aria-hidden="true"
                style={{
                    width: 16, height: 16, flex: '0 0 auto', marginTop: description ? 2 : 0, borderRadius: 5,
                    border: `1px solid ${on ? 'var(--burgundy-700)' : 'var(--border-strong)'}`,
                    background: on ? 'var(--burgundy-700)' : 'var(--surface)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'var(--transition-control)',
                }}
            >
                {indeterminate ? (
                    <span style={{ width: 8, height: 2, background: '#fff', borderRadius: 1 }} />
                ) : checked ? (
                    <span style={{ width: 9, height: 5, borderLeft: '2px solid #fff', borderBottom: '2px solid #fff', transform: 'rotate(-45deg) translate(1px,-1px)' }} />
                ) : null}
            </span>
            {/* O <input> real fica visualmente oculto, mas continua no fluxo de
                foco e é quem o leitor de tela anuncia. */}
            <input
                type="checkbox" checked={checked} disabled={disabled}
                aria-label={label ? undefined : ariaLabel}
                ref={(el) => { if (el) el.indeterminate = indeterminate; }}
                onChange={(e) => onChange?.(e.target.checked)}
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
            />
            {label && (
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 14, lineHeight: '20px', color: 'var(--text-primary)' }}>{label}</span>
                    {description && <span style={{ fontSize: 12.5, lineHeight: '17px', color: 'var(--text-secondary)' }}>{description}</span>}
                </span>
            )}
        </label>
    );
}
